import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { isEmpty, isNil } from "ramda";
import { styled } from "styled-components";

import { InputField } from "affinityForm/fields";
import { Button } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { Layout } from "affinityUI/structures/layout";
import { loginUser } from "lib/auth";
import { Yup } from "lib/extendYup";

import { Error, ErrorPanel } from "../components/styled";

const loginValidationSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const LoginMetaGrid = styled(Grid)`
  margin-top: -1rem;
`;

const getCurrentURL = () => {
  return window.location.href;
};

const getCurrentURLOrigin = () => {
  return new URL(getCurrentURL()).origin;
};

const isRelativeURL = (targetURL: string | URL) =>
  getCurrentURLOrigin() === new URL(targetURL, getCurrentURLOrigin()).origin;
const isExternalURL = (targetURL: string | URL) => new URL(targetURL).origin !== getCurrentURLOrigin();
const isTargetExternalURL = (targetURL: string | URL) => !isRelativeURL(targetURL) && isExternalURL(targetURL);

export const LoginPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const target = searchParams.get("target");

  useEffect(() => {
    const storageListener = (e: StorageEvent) => {
      if (e.key === "logoutToken" && isNil(e.oldValue) && e.newValue) {
        window.location.replace(
          isNil(target) || isEmpty(target) || isTargetExternalURL(target) ? getCurrentURLOrigin() : target
        );
      }
    };
    window.addEventListener("storage", storageListener);
    return () => {
      window.removeEventListener("storage", storageListener);
    };
  });

  const handleSubmit = useCallback(
    async (data) => {
      setError(null);
      const result = await loginUser(data);

      switch (result.responseStatus) {
        case "OK":
          window.location.replace(
            isNil(target) || isEmpty(target) || isTargetExternalURL(target) ? getCurrentURLOrigin() : target
          );
          return;
        case "400":
          setError("Email or password is not correct");
          return;
        default:
          setError("Something went wrong");
          return;
      }
    },
    [target]
  );

  const handleSubmitClick = useCallback(() => setError(null), [setError]);

  return (
    <Layout title="Login" publicLayout>
      <Formik
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={loginValidationSchema}
        initialValues={{}}
        onSubmit={handleSubmit}
      >
        <Form>
          {error && (
            <ErrorPanel data-testid="login-error">
              <Error>{error}</Error>
            </ErrorPanel>
          )}
          <InputField name="username">Email</InputField>
          <InputField name="password" type="password">
            Password
          </InputField>
          <LoginMetaGrid>
            <Link to="/login/recover">Recover password</Link>
            <Button type="submit" onClick={handleSubmitClick}>
              Login
            </Button>
            <p>Contact Entia for more information about the Liberty service.</p>
          </LoginMetaGrid>
        </Form>
      </Formik>
    </Layout>
  );
};
